import {
  ChessJsRulesPort,
  type RulesColor,
  type RulesPiece,
} from '@/game/rules';
import type { AcceptedSessionMove } from '@/game/session';

export type PieceTheme = 'capy' | 'standard';

export interface CapturedMaterial {
  readonly w: readonly RulesPiece[];
  readonly b: readonly RulesPiece[];
}

export type CapturedMaterialProjection =
  | { readonly status: 'ready'; readonly lostByColor: CapturedMaterial }
  | { readonly status: 'unavailable'; readonly lostByColor: CapturedMaterial };

const UCI_PATTERN = /^([a-h][1-8])([a-h][1-8])([qrbn])?$/;

const PIECE_CODE: Readonly<Record<RulesPiece, string>> = {
  p: 'P',
  n: 'N',
  b: 'B',
  r: 'R',
  q: 'Q',
  k: 'K',
};

function emptyCapturedMaterial(): CapturedMaterial {
  return Object.freeze({
    w: Object.freeze([]),
    b: Object.freeze([]),
  });
}

function unavailable(): CapturedMaterialProjection {
  return Object.freeze({ status: 'unavailable', lostByColor: emptyCapturedMaterial() });
}

/**
 * Replays accepted session events through the existing chess.js rules adapter.
 * The UI never infers captures from SAN or board subtraction, and any invalid
 * history fails closed instead of displaying a plausible-but-wrong tray.
 */
export function projectCapturedMaterial(
  initialFen: string,
  acceptedMoves: readonly Pick<AcceptedSessionMove, 'uci'>[],
): CapturedMaterialProjection {
  let rules: ChessJsRulesPort;
  try {
    rules = new ChessJsRulesPort(initialFen);
  } catch {
    return unavailable();
  }

  const lostByColor: Record<RulesColor, RulesPiece[]> = { w: [], b: [] };
  for (const move of acceptedMoves) {
    const parsed = UCI_PATTERN.exec(move.uci);
    if (parsed === null) {
      return unavailable();
    }
    const [, from, to, promotion] = parsed;
    const before = rules.snapshot();
    const attempt = rules.submit({
      expectedRevision: before.revision,
      from: from!,
      to: to!,
      ...(promotion === undefined ? {} : { promotion: promotion as 'q' | 'r' | 'b' | 'n' }),
    });
    if (!attempt.accepted) {
      return unavailable();
    }
    if (attempt.value.verbose.captured !== undefined) {
      const capturedColor: RulesColor = attempt.value.verbose.color === 'w' ? 'b' : 'w';
      lostByColor[capturedColor].push(attempt.value.verbose.captured);
    }
  }

  return Object.freeze({
    status: 'ready',
    lostByColor: Object.freeze({
      w: Object.freeze([...lostByColor.w]),
      b: Object.freeze([...lostByColor.b]),
    }),
  });
}

export function pieceAssetUrl(
  theme: PieceTheme,
  color: RulesColor,
  piece: RulesPiece,
): string {
  const directory = theme === 'capy' ? 'capychess' : 'standard';
  return `${import.meta.env.BASE_URL}piece/${directory}/${color}${PIECE_CODE[piece]}.${theme === 'capy' ? 'webp' : 'svg'}`;
}
